/**
 * Normalizes an axios error thrown by the Team & User Management API
 * into whatever your existing errorHandler(error, text, navigate)
 * already expects — a string OR a { field: message } object — EXCEPT
 * for the one shape errorHandler was never built to see: the 429
 * rate-limit response, which skips the { status, success, errors }
 * envelope entirely and comes back as a bare { message }.
 *
 * Usage at a call site:
 *   try {
 *     await clientTeamInviteUser(payload);
 *   } catch (err) {
 *     errorHandler(getTeamApiError(err), "", navigate);
 *   }
 */
export function getTeamApiError(error) {
	const res = error?.response;

	if (!res) {
		// Never reached the server — network error, timeout, CORS, etc.
		return "Something went wrong. Please check your internet connection and try again.";
	}

	if (res.status === 429) {
		// Doc: "This response does not use the standard envelope (no
		// status/success/errors — just a bare message)."
		return (
			res.data?.message ||
			"Too many attempts. Please try again in 15 minutes."
		);
	}

	// Normal envelope: { status, success:false, errors }. `errors` is
	// either a string (Joi validator, most endpoints) or an object of
	// { fieldName: message } (older validator, a couple of endpoints +
	// login). Pass either straight through to errorHandler, which
	// already branches on typeof.
	if (res.data && "errors" in res.data) {
		return res.data.errors;
	}

	return res.data?.message || "Something went wrong. Please try again.";
}

/** True only for the specific "email already used elsewhere on the platform" 409. */
export function isDuplicateEmailError(error) {
	return (
		error?.response?.status === 409 &&
		typeof error?.response?.data?.errors === "string" &&
		error.response.data.errors.toLowerCase().includes("already associated")
	);
}